import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dividendTreatmentLabel, type Context, type ReportResult } from '@hjci-investorhub-v2/domain';
import { api, useApi } from '../../lib/api';
import { ErrorNotice, Loading, PageHeading } from '../../components/ui/common';
import { ReportWarnings } from './report-warnings';
import { PerformanceTable } from './performance-table';
import { PerformanceFilters, type MatrixReference } from './performance-filters';
import { filterParams, matrixRequest, readFilters } from './matrix-filters';

export function PerformancePage() {
  const [params, setParams] = useSearchParams();
  const key = params.toString();
  const filters = useMemo(() => readFilters(new URLSearchParams(key)), [key]);
  const [context, setContext] = useState<Context>('unrealised');
  const reference = useApi<MatrixReference>('/reports/filter-options');
  const request = matrixRequest(filters);
  const validDates = Boolean(filters.startDate && filters.endDate && filters.startDate <= filters.endDate);
  const report = useQuery({ queryKey: ['report', request], queryFn: ({ signal }) => api<ReportResult>('/reports/calculate', { method: 'POST', body: JSON.stringify(request), headers: { 'x-report-view': 'matrix' }, signal }), enabled: validDates, staleTime: 0, refetchOnMount: 'always' });
  return <><PageHeading title="Investment Performance" description="View detailed performance metrics for portfolios, subfolios, and shares." />
    <PerformanceFilters key={key} filters={filters} reference={reference.data} onApply={value => {
      const next = filterParams(value);
      if (JSON.stringify(matrixRequest(value)) === JSON.stringify(request)) void report.refetch();
      if (next.toString() !== key) setParams(next);
    }} />
    {reference.error && <ErrorNotice error={reference.error} />}{report.error && <ErrorNotice error={report.error} retry={() => void report.refetch()} />}
    {report.isPending && validDates ? <Loading text="Calculating dated holdings and returns…" /> : report.data && <>
      <ReportWarnings warnings={report.data.warnings} reference={reference.data} />
      <PerformanceTable key={key} report={report.data} context={context} onContext={setContext} filters={filters} reference={reference.data} refreshing={report.isFetching} onRefresh={() => void report.refetch()} />
      <p className="calculation-footer">Calculation {report.data.calculationVersion} · Dividend treatment: {dividendTreatmentLabel(report.data.dividendConvention)} · Dated holdings include posted corporate actions.</p>
    </>}
  </>;
}
